import React, { FC, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import L from 'leaflet';
import { Map as LeafletMap, ImageOverlay } from 'react-leaflet';
import HeatmapLayer from 'react-leaflet-heatmap-layer';

import { IHeatMap } from '../../../models/reportHeatMap';
import { IReportHeatMapPoint } from '../../../models/types';
import { getOverlayCornersFromGbox, isValidGbox } from '../../../utils/planCorners.utils';
import WMSTileLayer from '../../Main/Map/WMSTileLayer';
import ReactDistortableImageOverlay from 'react-leaflet-distortable-imageoverlay';
import { Dispatch, iRootState } from '../../../store';
import { connect } from 'react-redux';
import Tooltip from '../../../components_/Tooltip';
import { Button } from 'antd';
import Icon from '../../../components_/Icon';
import Grid from '@material-ui/core/Grid';

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    marginTop: spacing(5),
    width: '100%',
    height: 700,
    zIndex: 0
  },
  layersButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 1000
  }
}));

const mapState = (state: iRootState) => ({
  control: state.reportHeatMap.control,
  isGeo: state.reportHeatMap.layers.isGeo
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    setControl: d.reportHeatMap.setControl,
    setLayers: d.reportHeatMap.setLayers
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type IProps = { heatMap: IHeatMap } & connectedProps;
const Map: FC<IProps> = ({ heatMap, control, setControl, setLayers, isGeo }) => {
  const classes = useStyles();
  const mapRef = useRef<LeafletMap | null>(null);

  const { map, planImg, data } = heatMap;
  const { preview, previewFormat, gbox, bbox } = map;
  const hasValidGbox = isGeo && isValidGbox(gbox);
  const LatLng = L.latLng as any;

  useEffect(() => {
    if (mapRef.current?.contextValue?.map && map.id) {
      const corners = getOverlayCornersFromGbox(gbox);
      mapRef.current?.contextValue?.map.fitBounds(
        corners
          ? corners.map(corner => new LatLng(corner.lat, corner.lng))
          : [new LatLng(bbox[1], bbox[0]), new LatLng(bbox[3], bbox[2])],
        {
          padding: [150, 150],
          maxZoom: 20
        }
      );
      const centerLL = mapRef.current?.contextValue?.map.getCenter();
      setControl({
        ...control,
        zoom: mapRef.current?.contextValue?.map.getZoom(),
        center: [centerLL.lat, centerLL.lng] as [number, number]
      });
    }
  }, [mapRef, isGeo, map.id, bbox, gbox]);

  // const imageOverlayUrl = getBlobImage(preview, previewFormat);

  const { center, bounds } = createCoord(bbox);
  const handleClick = () => () => {
    setLayers({ isGeo: !isGeo });
  };

  const geoOverlayCorners = getOverlayCornersFromGbox(gbox);

  return (
    <LeafletMap
      ref={mapRef}
      center={control.center}
      crs={isGeo ? L.CRS.EPSG3857 : L.CRS.Simple}
      zoom={isGeo ? control.zoom : 4}
      minZoom={-5}
      className={classes.root}
      attributionControl={false}
    >
      {!isGeo ? (
        <ImageOverlay url={planImg} bounds={bounds} zIndex={0} />
      ) : hasValidGbox && geoOverlayCorners ? (
        <>
          <ReactDistortableImageOverlay
            key={map.contentId}
            url={planImg}
            opacity={1}
            editMode={false}
            corners={geoOverlayCorners.map(corner => new LatLng(corner.lat, corner.lng))}
          />
          <WMSTileLayer />
        </>
      ) : (
        <WMSTileLayer />
      )}
      <HeatmapLayer
        points={data}
        latitudeExtractor={(point: IReportHeatMapPoint) => (isGeo ? point.lat : point.y)}
        longitudeExtractor={(point: IReportHeatMapPoint) => (isGeo ? point.lon : point.x)}
        intensityExtractor={(point: IReportHeatMapPoint) => point.weightNormalized}
        radius={10}
        blur={1}
        max={0.01}
        maxZoom={100}
        minOpacity={0.2}
        //   const gradient = { 0.4: "blue", 0.8: "blue", 1.0: "blue" };
        // const gradient2 = { 0.4: "red", 0.8: "red", 1.0: "red" };
        // const gradient3 = { 0.4: "green", 0.8: "green", 1.0: "green" };
        // const gradient4 = { 0.4: "orange", 0.8: "orange", 1.0: "orange" };
        // gradient={{
        //   0.1: mapColor.normalBlackberry,
        //   0.3: mapColor.darkBlueberry,
        //   0.5: mapColor.darkestBanana,
        //   0.8: mapColor.darkestOrange,
        //   1.0: mapColor.mtsRed
        // }}
        gradient={{
          0.2: 'blue',
          0.5: 'green',
          0.7: 'yellow',
          1.0: 'red'
        }}
      />
      <Grid className={classes.layersButton}>
        <Tooltip size="l" label={isGeo ? 'Свернуть план' : 'Развернуть план'}>
          <Button
            icon={
              isGeo ? <Icon name={'compressRight'} size="m" /> : <Icon name={'scale'} size="m" />
            }
            // disabled={mode !== AreaModes.Default}
            key={'isGeo'}
            size={'large'}
            onClick={handleClick()}
          />
        </Tooltip>
      </Grid>
    </LeafletMap>
  );
};

const createCoord = ([x, y, z, w]: [number, number, number, number]) => {
  const center = [w + y, z + x].map(coord => coord / 2) as [number, number];
  const bounds = [
    [w, x],
    [y, z]
  ] as [[number, number], [number, number]];

  return { center, bounds };
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Map);
